import { useMemo, useState, useEffect } from 'react';
import {
  Box,
  Button,
  IconButton,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

interface CalendarProps {
  selected: string | null;  // yyyymmdd format, single date
  onChange: (next: string | null) => void;
  available: string[];  // yyyymmdd dates that are clickable
}

function ymdToDate(ymd: string): Date | null {
  if (!/^\d{8}$/.test(ymd)) return null;
  return new Date(
    Number(ymd.slice(0, 4)),
    Number(ymd.slice(4, 6)) - 1,
    Number(ymd.slice(6, 8))
  );
}

function dateToYmd(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}${m}${d}`;
}

function buildCalendarMatrix(displayedMonth: Date): Date[] {
  const startOfMonth = new Date(displayedMonth.getFullYear(), displayedMonth.getMonth(), 1);
  const startDayOfWeek = startOfMonth.getDay();
  const gridStart = new Date(startOfMonth);
  gridStart.setDate(startOfMonth.getDate() - startDayOfWeek);

  const days: Date[] = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(gridStart);
    d.setDate(gridStart.getDate() + i);
    days.push(d);
  }
  return days;
}

const WEEKDAY_LABELS = ['日', '一', '二', '三', '四', '五', '六'];

export default function Calendar({ selected, onChange, available }: CalendarProps) {
  const theme = useTheme();
  const availableSet = useMemo(() => new Set(available || []), [available]);

  const getLatestAvailable = () => {
    if (!available?.length) return new Date();
    const latest = available.reduce((a, b) => (a > b ? a : b));
    return ymdToDate(latest) || new Date();
  };

  const [displayedMonth, setDisplayedMonth] = useState<Date>(() => {
    if (selected) {
      const selectedDate = ymdToDate(selected);
      if (selectedDate) return new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1);
    }
    const latest = getLatestAvailable();
    return new Date(latest.getFullYear(), latest.getMonth(), 1);
  });

  // Sync displayed month when selected date changes externally
  useEffect(() => {
    if (selected) {
      const selectedDate = ymdToDate(selected);
      if (selectedDate) {
        const newMonth = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1);
        if (newMonth.getTime() !== displayedMonth.getTime()) {
          setDisplayedMonth(newMonth);
        }
      }
    }
  }, [selected]);

  const gridDays = useMemo(() => buildCalendarMatrix(displayedMonth), [displayedMonth]);
  const monthLabel = `${displayedMonth.getFullYear()}年${displayedMonth.getMonth() + 1}月`;

  const isSameMonth = (date: Date, base: Date): boolean => {
    return date.getFullYear() === base.getFullYear() && date.getMonth() === base.getMonth();
  };

  const selectDay = (date: Date) => {
    const ymd = dateToYmd(date);
    if (!availableSet.has(ymd)) return;
    // Single select: if already selected, deselect; otherwise select new date
    onChange(selected === ymd ? null : ymd);
  };

  return (
    <Box sx={{ width: '100%', userSelect: 'none' }}>
      {/* Header with navigation */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <IconButton
          size="small"
          onClick={() => setDisplayedMonth(new Date(displayedMonth.getFullYear(), displayedMonth.getMonth() - 1, 1))}
          sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}
        >
          <ChevronLeftIcon fontSize="small" />
        </IconButton>
        <Typography variant="body2" fontWeight={500}>
          {monthLabel}
        </Typography>
        <IconButton
          size="small"
          onClick={() => setDisplayedMonth(new Date(displayedMonth.getFullYear(), displayedMonth.getMonth() + 1, 1))}
          sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}
        >
          <ChevronRightIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Weekday labels */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5, mb: 0.5 }}>
        {WEEKDAY_LABELS.map((label) => (
          <Typography
            key={label}
            variant="caption"
            sx={{ textAlign: 'center', color: 'text.secondary' }}
          >
            {label}
          </Typography>
        ))}
      </Box>

      {/* Calendar grid */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5 }}>
        {gridDays.map((d) => {
          const ymd = dateToYmd(d);
          const enabled = availableSet.has(ymd);
          const isSelected = selected === ymd;
          const inMonth = isSameMonth(d, displayedMonth);

          return (
            <Button
              key={ymd}
              size="small"
              onClick={() => selectDay(d)}
              disabled={!enabled}
              aria-pressed={isSelected}
              sx={{
                minWidth: 0,
                aspectRatio: '1',
                p: 0,
                fontSize: '0.75rem',
                borderRadius: 1,
                border: 1,
                borderColor: isSelected ? 'transparent' : 'divider',
                bgcolor: isSelected
                  ? 'primary.main'
                  : alpha(theme.palette.background.paper, 0.6),
                color: isSelected
                  ? 'primary.contrastText'
                  : inMonth
                    ? 'text.primary'
                    : 'text.disabled',
                opacity: !inMonth && !isSelected ? 0.4 : 1,
                '&:hover': {
                  bgcolor: isSelected
                    ? 'primary.dark'
                    : alpha(theme.palette.action.hover, 0.1),
                },
                '&.Mui-disabled': {
                  opacity: 0.3,
                  color: 'text.disabled',
                },
              }}
            >
              {d.getDate()}
            </Button>
          );
        })}
      </Box>
    </Box>
  );
}
